import { inRange } from '../../core/utils/general/general';
import { ReturnTypeofSheetSectionsSelector } from '../selectors/sheetUiSelector';
import { Area, Freeze, Viewport } from '../types';
import {
  getFreezeBarHandleSize,
  getSizeForViewportIndex,
} from '../utils/viewport/viewportUtils';
import { getSheetCellsLeft } from './sheetRTLHelper';
import { getViewportStartEnd } from '../utils/views/virtualViewport';
import { doesRangeInterects } from '../utils/area/range';

export interface VisibleArea {
  x: number;
  y: number;
  width: number;
  height: number;
  borderTop: boolean;
  borderBottom: boolean;
  borderLeft: boolean;
  borderRight: boolean;
}

export const getVisibleSelectionAreas = (
  uiProps: Omit<
    ReturnTypeofSheetSectionsSelector,
    'selections' | 'activeCell' | 'selecting'
  >,
  originalArea: Area,
  stageWidth: number,
  isRtl: boolean
): VisibleArea[] => {
  const {
    freezedCols,
    freezedRows,
    defaultColWidth,
    defaultRowHeight,
    colHeaderHeight,
    colFilterHeight,
    rowHeaderWidth,
    rowViewport,
    colViewport,

    zoom,
  } = uiProps;
  const { start, end } = originalArea;

  const { start: frs, end: fre } = getViewportStartEnd(
    rowViewport.frozenIndexes
  );
  const { start: rs, end: re } = getViewportStartEnd(
    rowViewport.nonFrozenIndexes
  );

  const { start: fcs, end: fce } = getViewportStartEnd(
    colViewport.frozenIndexes
  );
  const { start: cs, end: ce } = getViewportStartEnd(
    colViewport.nonFrozenIndexes
  );

  // These values are already zoomed values from sheetUiSelectors
  const top = colHeaderHeight + colFilterHeight; //  groupBarHeight +
  const left = getSheetCellsLeft(stageWidth, rowHeaderWidth, isRtl);

  // Start Split Areas
  let areas: Area[] = [originalArea];
  if (
    freezedRows.frozen &&
    doesRangeInterects(
      {
        start: start.ri,
        end: end.ri,
      },
      freezedRows
    )
    // inRange(start.ri, freezedRows.start, freezedRows.end)
  ) {
    areas = splitViewportAreas(
      originalArea,
      'ri',
      freezedRows.end,
      freezedRows.start,
      re
    );
  }

  if (
    freezedCols.frozen &&
    doesRangeInterects(
      {
        start: start.ci,
        end: end.ci,
      },
      freezedCols
    )
    // inRange(start.ci, freezedCols.start, freezedCols.end)
  ) {
    areas = areas.flatMap((area) =>
      splitViewportAreas(area, 'ci', freezedCols.end, freezedCols.start, ce)
    );
  }

  // Create Visible Areas
  return areas.map((splittedArea, index) => {
    const {
      placement: y,
      size: height,
      borderStart: borderTop,
      borderEnd: borderBottom,
    } = createVisibleAreas(
      rowViewport,
      defaultRowHeight,
      splittedArea,
      originalArea,
      freezedRows,
      top,
      'ri',
      frs,
      fre,
      rs,
      re,
      false,
      zoom
    );

    const {
      placement: x,
      size: width,
      borderStart: borderLeft,
      borderEnd: borderRight,
    } = createVisibleAreas(
      colViewport,
      defaultColWidth,
      splittedArea,
      originalArea,
      freezedCols,
      left,
      'ci',
      fcs,
      fce,
      cs,
      ce,
      isRtl,
      zoom
    );

    return {
      x,
      y,
      width,
      height,
      borderTop,
      borderBottom,
      borderLeft,
      borderRight,
    };
  });
};

export const createVisibleAreas = (
  virtualViewport: Viewport,
  defaultSize: number,
  { start, end }: Area,
  originalArea: Area,
  freezed: Freeze,
  // freezedSum: number,
  initialPlacement: number,
  alias: 'ri' | 'ci',
  freezeStart: number,
  freezeEnd: number,
  viewportStart: number,
  viewportEnd: number,
  isRtl: boolean,
  zoom: number
) => {
  let placementBefore = 0,
    vs = 0,
    ve = 0,
    areaStart = 0,
    areaEnd = 0,
    viewportIndexes = [],
    viewportSizes = [];

  if (
    freezed.frozen &&
    // inRange(start[alias], freezed.start, freezed.end)
    doesRangeInterects(
      {
        start: start[alias],
        end: end[alias],
      },
      freezed
    )
  ) {
    placementBefore = initialPlacement;
    vs = freezeStart;
    ve = freezeEnd;
    areaStart = Math.max(start[alias], vs);
    areaEnd = Math.min(end[alias], ve);
    viewportIndexes = virtualViewport.frozenIndexes;
    viewportSizes = virtualViewport.frozenSizes;
  } else {
    const op = isRtl ? -1 : 1;
    placementBefore =
      initialPlacement +
      op * (getFreezeBarHandleSize(freezed, zoom) + virtualViewport.frozenSum); // + freezedSum
    vs = viewportStart;
    ve = viewportEnd;

    areaStart = Math.max(start[alias], viewportStart);
    areaEnd = Math.min(end[alias], viewportEnd);

    viewportIndexes = virtualViewport.nonFrozenIndexes;
    viewportSizes = virtualViewport.nonFrozenSizes;
  }

  return createVisibleArea(
    placementBefore,
    defaultSize,
    vs,
    ve,
    viewportIndexes,
    viewportSizes,
    areaStart,
    areaEnd,
    originalArea.start[alias],
    originalArea.end[alias],
    isRtl,
    zoom
  );
};

const createVisibleArea = (
  posBefore: number,
  defaultSize: number,
  viewportStart: number, //In case of freeze it is freeze.start
  viewportEnd: number, // #
  viewportIndexes: number[],
  viewportSizes: number[],
  areaStart: number, // contains area within viewport actual area can be greater than viewport
  areaEnd: number, // #
  originalAreaStart: number, // Original user selected area
  originalAreaEnd: number,
  isRtl: boolean,
  zoom: number
) => {
  const dimensions = getAreaPlacement(
    viewportIndexes,
    viewportSizes,
    areaStart,
    areaEnd,
    defaultSize,
    zoom
  );

  const placement = isRtl
    ? posBefore - dimensions.size - dimensions.placement
    : posBefore + dimensions.placement;

  const borderStart = originalAreaStart >= viewportStart;
  const borderEnd = originalAreaEnd <= viewportEnd;

  return {
    placement,
    size: dimensions.size,
    borderStart,
    borderEnd,
  };
};

// Split areas into freezed | non freezed
const splitViewportAreas = (
  area: Area,
  splitBy: 'ci' | 'ri',
  splitIndex: number,
  viewportStart: number,
  viewportEnd: number
) => {
  const { start, end } = area;
  const areas: Area[] = [];
  areas.push({
    start: { ...start },
    end: {
      ...end,
      [splitBy]: Math.min(splitIndex, end[splitBy]),
    },
  });

  if (area.end[splitBy] > splitIndex) {
    areas.push({
      start: { ...start, [splitBy]: Math.max(splitIndex + 1, viewportStart) },
      end: {
        ...end,
        [splitBy]: Math.min(end[splitBy], viewportEnd),
      },
    });
  }

  return areas;
};

const getAreaPlacement = (
  viewportIndexes: number[],
  viewportSizes: number[],
  areaStart: number,
  areaEnd: number,
  defaultSize: number,
  zoom: number
) => {
  let placement = 0;
  let size = 0;

  const { start, end } = getViewportStartEnd(viewportIndexes);

  areaStart = Math.max(start, areaStart);

  if (!inRange(areaStart, start, end))
    return {
      placement: 0,
      size: 0,
    };

  for (let [index, viewportIndex] of viewportIndexes.entries()) {
    // const item = entries[viewportIndex];
    // const itemSize = getRowColSize(defaultSize, item, zoom);

    const itemSize = getSizeForViewportIndex(
      viewportSizes,
      index,
      defaultSize,
      zoom
    );

    if (inRange(viewportIndex, areaStart, areaEnd)) {
      size += itemSize;
    }

    if (viewportIndex < areaStart) {
      placement += itemSize;
    }
  }

  return {
    placement,
    size,
  };
};
